import { ConfigurableToolInputSchemas } from "@app/lib/actions/mcp_internal_actions/input_schemas";
import type { ServerMetadata } from "@app/lib/actions/mcp_internal_actions/tool_definition";
import { INTERNAL_MIME_TYPES } from "@dust-tt/client";
import { z } from "zod";

// Schema for tool_without_user_config
const toolWithoutUserConfigSchema = {
  query: z.string(),
};

// Schema for pass_through - demonstrates various configurable input types
const passThroughSchema = {
  query: z.string(),
  user: z.object({
    name: ConfigurableToolInputSchemas[INTERNAL_MIME_TYPES.TOOL_INPUT.STRING]
      .describe("The name of the user")
      .default({
        value: "John Doe",
        mimeType: INTERNAL_MIME_TYPES.TOOL_INPUT.STRING,
      }),
    age: ConfigurableToolInputSchemas[INTERNAL_MIME_TYPES.TOOL_INPUT.NUMBER]
      .describe("The age of the user")
      .default({
        value: 30,
        mimeType: INTERNAL_MIME_TYPES.TOOL_INPUT.NUMBER,
      }),
    admin: ConfigurableToolInputSchemas[INTERNAL_MIME_TYPES.TOOL_INPUT.BOOLEAN]
      .describe("Whether the user is an admin")
      .default({
        value: true,
        mimeType: INTERNAL_MIME_TYPES.TOOL_INPUT.BOOLEAN,
      }),
    location: ConfigurableToolInputSchemas[
      INTERNAL_MIME_TYPES.TOOL_INPUT.STRING
    ].describe("The location of the user"),
    enabled: ConfigurableToolInputSchemas[
      INTERNAL_MIME_TYPES.TOOL_INPUT.BOOLEAN
    ].describe("Whether the user is enabled"),
    category: z
      .object({
        value: z.enum(["A", "B", "C"]),
        mimeType: z.literal(INTERNAL_MIME_TYPES.TOOL_INPUT.ENUM),
      })
      .describe("The category of the user"),
  }),
  choices: z
    .object({
      options: z
        .union([
          z
            .object({
              value: z.literal("A"),
              label: z.literal("Label A"),
            })
            .describe("The label of the choice"),
          z
            .object({
              value: z.literal("B"),
              label: z.literal("Label B"),
            })
            .describe("The label of the choice"),
          z
            .object({
              value: z.literal("C"),
              label: z.literal("Label C"),
            })
            .describe("The label of the choice"),
        ])
        // Options are optional because we only need them for the UI but they won't be provided when the tool is called.
        .optional(),
      // "values" are required because they are used to provide the selected values when the tool is called.
      values: z.array(z.string()).describe("The values of the choices"),
      mimeType: z.literal(INTERNAL_MIME_TYPES.TOOL_INPUT.LIST),
    })
    .describe("Indicate the choices the agent can select from"),
};

// Tools metadata
export const PRIMITIVE_TYPES_DEBUGGER_TOOLS_METADATA = [
  {
    name: "tool_without_user_config",
    description: "Test the tool without user config.",
    schema: toolWithoutUserConfigSchema,
    stake: "high",
    displayLabels: {
      running: "Running debug tool",
      done: "Run debug tool",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
  {
    name: "pass_through",
    description: "Pass through inputs for primitive type debugging.",
    schema: passThroughSchema,
    stake: "high",
    displayLabels: {
      running: "Passing through",
      done: "Pass through",
    },
    toolCostCategory: "basic",
    freeUsage: true,
  },
] as const;

// Server metadata - used in constants.ts
export const PRIMITIVE_TYPES_DEBUGGER_SERVER = {
  serverInfo: {
    name: "primitive_types_debugger",
    version: "1.0.0",
    description:
      "Demo server showing a basic interaction with various configurable blocks.",
    icon: "ActionEmotionLaughIcon",
    authorization: null,
    documentationUrl: null,
  },
  tools: PRIMITIVE_TYPES_DEBUGGER_TOOLS_METADATA,
} as const satisfies ServerMetadata;
